import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import {
  createOzonAutomation,
  createOzonAutomationsBatch,
  getOzonAutomationLimitsContext,
  getOzonAutomationProducts,
  getOzonAutomations,
  setOzonAutomationStatus,
  updateOzonAutomation,
} from '../../../../services/ozonBidder';
import { bulkOzonAutomations, getOzonAutomationStrategies } from '../../../../services/ozonBidder';
import './OzonAutomationsTab.css';
import OzonAutomationDashboard from './OzonAutomationDashboard';
import OzonAutomationsTable from './OzonAutomationsTable';
import { filterBySearch } from '../../../../utils/search';

const FALLBACK_STRATEGIES = [
  { key: 'PROFIT_GROWTH', title: 'Рост прибыли', description: 'Растить абсолютную прибыль, расход ставки оправдан только отдачей' },
];

const LEVER_HINTS = {
  BID: 'Менять ставку CPC на выбранных парах РК–товар',
  PRICE: 'Менять цену товара внутри заданного коридора',
};

const MODES = [
  ['AUTO', 'Автоматический — система ведёт всё сама, вы читаете журнал'],
  ['CONTROLLED', 'С подтверждением — вы нажимаете кнопку на каждое изменение'],
  ['OBSERVE', 'Наблюдение — система ничего не отправляет, только считает'],
];
const MODE_NOTE = {
  AUTO: 'Кнопку нажимает система. Она сама выбирает гипотезу, меняет ставку или цену, '
    + 'ведёт окно наблюдения, выносит вердикт и откатывает то, что ухудшило результат. '
    + 'Всё записывается в журнал: видно, что и почему поменялось.',
  CONTROLLED: 'Кнопку нажимаете вы. Система готовит конкретное действие и ждёт '
    + 'подтверждения в карточке автоматизации.',
  OBSERVE: 'Кнопку не нажимает никто. Режим для того, чтобы присмотреться к данным '
    + 'перед запуском; сам по себе он ничего не меняет.',
};

// ДРР хранится долей (0.15), а на экране все остальные проценты — процентами.
// Одно поле, живущее по другому правилу, — прямой путь ввести 15 и получить
// отказ, поэтому конвертация делается здесь, а не в голове пользователя.
const FRACTION_FIELDS = ['target_drr'];
const toPercent = value => (value === '' || value == null ? '' : Math.round(Number(value) * 1e6) / 1e4);
const toFraction = value => (value === '' || value == null ? null : Math.round(Number(value) * 1e4) / 1e6);

const constraintsForScreen = constraints => {
  const result = { ...constraints };
  FRACTION_FIELDS.forEach(key => { result[key] = toPercent(result[key]); });
  result.per_sku = Object.fromEntries(Object.entries(constraints.per_sku || {}).map(
    ([sku, row]) => [sku, Object.fromEntries(Object.entries(row).map(
      ([key, value]) => [key, FRACTION_FIELDS.includes(key) ? toPercent(value) : value]))]));
  return result;
};

const constraintsForWire = constraints => {
  const result = { ...constraints };
  FRACTION_FIELDS.forEach(key => { result[key] = toFraction(result[key]); });
  return result;
};

const freshDraft = source => ({
  name: '', strategy_type: '', sku_ids: [], managed_sku_ids: [], control_targets: [],
  status: 'ACTIVE', execution_mode: 'AUTO', target_mode: 'AUTO', source,
  positioning: { fragments: [], dictionary_id: null, dictionary_name: '' },
  objective: { target_value: '', target_date: '', secondary_goal: 'PROFIT',
    position_source: 'POSITION', reduce_target: 'orders', period_days: 30 },
  constraints: {
    experiment_budget: 5000, max_actions_per_day: 1, auto_rollback: true,
    per_sku: {},
    max_bid: '', bid_change_limit_pct: 5,
    min_price: '', max_price: '', price_change_limit_pct: 3,
    target_drr: '', target_margin_pct: '', min_margin_pct: '',
  },
  permissions: { bid: true, price: false, budget: false, jev: false },
  evaluation: { meaningful_change_pct: 5, min_days: 7, max_days: 14 },
});

// Те же ценовые диапазоны, что и по умолчанию в «Генераторе РК» — незачем
// придумывать другие границы для того же товарного ассортимента.
const PRICE_RANGES = [
  { min_price: 0, max_price: 500, label: 'до 500 ₽' },
  { min_price: 500, max_price: 2000, label: '500–2 000 ₽' },
  { min_price: 2000, max_price: null, label: 'от 2 000 ₽' },
];

const readError = error => {
  const detail = error?.detail;
  if (typeof detail === 'string') return detail;
  return detail?.message || error?.message || 'Не удалось сохранить автоматизацию';
};

export default function OzonAutomationsTab({ accountId, request, linkSearch = '' }) {
  const [automations, setAutomations] = useState([]);
  const [products, setProducts] = useState([]);
  const [strategies, setStrategies] = useState(FALLBACK_STRATEGIES);
  const [loading, setLoading] = useState(true);
  const [editor, setEditor] = useState(null);
  const [editingId, setEditingId] = useState(null);
  const [expandedId, setExpandedId] = useState(null);
  // Ссылка из уведомления: ?automation=<группа>&automation_tab=<вкладка>
  // раскрывает группу на нужной вкладке — и при открытии раздела, и по клику
  // на уведомление, когда раздел уже открыт.
  const [deepTab, setDeepTab] = useState(null);
  const handledLink = useRef('');
  const [listView, setListView] = useState(() => {
    try { return localStorage.getItem('oz-auto-list-view') || 'cards'; } catch { return 'cards'; }
  });
  const changeListView = value => {
    setListView(value);
    try { localStorage.setItem('oz-auto-list-view', value); } catch { /* per-viewer convenience only */ }
  };
  const [draft, setDraft] = useState(freshDraft('WIZARD'));
  const [step, setStep] = useState(1);
  const [search, setSearch] = useState('');
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState('');
  // В ручном режиме шагов нет, но и вываливать пять разделов разом незачем:
  // открыт один, остальные свёрнуты и подписаны итогом.
  const [openSection, setOpenSection] = useState(1);
  const [priceContext, setPriceContext] = useState(null);
  const [priceError, setPriceError] = useState('');
  // Мастер массовой настройки: как резать выбранный пул товаров на группы,
  // точечные переопределения по конкретной группе и статус пакетного создания.
  const [groupBy, setGroupBy] = useState('per_model');
  const [groupOverrides, setGroupOverrides] = useState({});
  // Явный выбор пользователя "создавать / не создавать" по конкретной группе —
  // отдельно от умолчания (которое уже снимает галочку с полностью занятых
  // существующей автоматизацией групп), чтобы пользователь мог передумать.
  const [groupToggles, setGroupToggles] = useState({});
  const [openGroupKey, setOpenGroupKey] = useState(null);
  const [batchSaving, setBatchSaving] = useState(false);
  const [batchProgress, setBatchProgress] = useState('');
  const [campaignPlan, setCampaignPlan] = useState(null);
  // Параметры, по которым построен план: очередь применения пересобирает его на сервере.
  const [campaignPlanBody, setCampaignPlanBody] = useState(null);
  const [campaignPlanLoading, setCampaignPlanLoading] = useState(false);
  const [campaignPlanError, setCampaignPlanError] = useState('');
  const [campaignApplyLoading, setCampaignApplyLoading] = useState(false);
  const [campaignApplyResult, setCampaignApplyResult] = useState(null);
  const [categoryFilters, setCategoryFilters] = useState(null);
  const [categoryFiltersLoading, setCategoryFiltersLoading] = useState(false);
  // With a single strategy there is nothing to choose and no goal to parametrise: steps 2 and 3 are skipped.
  const skipGoal = strategies.length <= 1;
  const wizardSteps = skipGoal ? [1, 4, 5, 6] : [1, 2, 3, 4, 5, 6];
  const WIZARD_TOTAL_STEPS = 6;
  const stepPosition = wizardSteps.indexOf(step) + 1;
  const stepAfter = delta => setStep(value => wizardSteps[Math.max(0, Math.min(wizardSteps.length - 1,
    wizardSteps.indexOf(value) + delta))]);
  const loadSequence = useRef(0);
  const strategyByKey = useMemo(
    () => Object.fromEntries(strategies.map(item => [item.key, item])), [strategies]);
  const spec = strategyByKey[draft.strategy_type] || null;
  const strategyLabel = key => strategyByKey[key]?.title || key;
  // The only strategy is the group's strategy from the start.
  useEffect(() => {
    if (strategies.length === 1 && editor && !draft.strategy_type) {
      setDraft(current => ({ ...current, strategy_type: strategies[0].key }));
    }
  }, [strategies, editor, draft.strategy_type]);
  // Only the levers this goal actually uses are offered, in its own order.
  const strategyLevers = spec?.levers || [];

  // `request` приложения пересоздаётся при каждом обновлении сессии. Список не должен из-за этого перезагружаться:
  // «Загрузка автоматизаций…» заменяла бы всю страницу и стирала несохранённые правки открытой группы.
  const requestRef = useRef(request);
  requestRef.current = request;
  const stableRequest = useCallback((...args) => requestRef.current(...args), []);
  const load = useCallback(async () => {
    const sequence = ++loadSequence.current;
    setLoading(true);
    setError('');
    try {
      const [automationData, productData, strategyData] = await Promise.all([
        getOzonAutomations({ request: stableRequest, accountId }),
        getOzonAutomationProducts({ request: stableRequest, accountId }),
        getOzonAutomationStrategies({ request: stableRequest }).catch(() => null),
      ]);
      if (sequence !== loadSequence.current) return;
      setAutomations(automationData.items || []);
      setProducts(productData.products || []);
      if (strategyData?.items?.length) setStrategies(strategyData.items);
    } catch (err) {
      if (sequence === loadSequence.current) setError(readError(err));
    } finally {
      if (sequence === loadSequence.current) setLoading(false);
    }
  }, [stableRequest, accountId]);

  useEffect(() => { load(); }, [load]);
  useEffect(() => { setEditor(null); setEditingId(null); setExpandedId(null); }, [accountId]);
  useEffect(() => {
    const params = new URLSearchParams(linkSearch || '');
    const target = params.get('automation');
    if (!target || handledLink.current === linkSearch) return;
    if (!automations.some(item => String(item.id) === target)) return;
    handledLink.current = linkSearch;
    setDeepTab({ tab: params.get('automation_tab') || null, at: Date.now() });
    setExpandedId(target);
    window.setTimeout(() => {
      const node = document.getElementById(`oz-auto-group-${target}`);
      if (node && node.scrollIntoView) node.scrollIntoView({ behavior: 'smooth', block: 'start' });
    }, 0);
  }, [linkSearch, automations]);

  const openEditor = (source, item = null) => {
    setDraft(item ? {
      ...freshDraft(source), ...item, source,
      positioning: { ...freshDraft(source).positioning, ...(item.positioning || {}) },
      objective: { ...freshDraft(source).objective, ...(item.objective || {}) },
      constraints: constraintsForScreen({
        ...freshDraft(source).constraints, ...(item.constraints || {}) }),
      permissions: { ...freshDraft(source).permissions, ...(item.permissions || {}) },
      evaluation: { ...freshDraft(source).evaluation, ...(item.evaluation || {}) },
    } : freshDraft(source));
    setEditingId(item?.id || null);
    setStep(source === 'MANUAL' ? 0 : 1);
    setSearch('');
    setError('');
    setGroupBy('per_model');
    setGroupOverrides({});
    setGroupToggles({});
    setOpenGroupKey(null);
    setCampaignPlan(null);
    setCampaignPlanBody(null);
    setCampaignPlanError('');
    setCampaignApplyResult(null);
    setCategoryFilters(null);
    setEditor(source);
  };

  const grouped = useMemo(() => {
    const result = {};
    filterBySearch(products, search)
      .forEach(item => {
        const key = item.model_name || 'Без модели';
        (result[key] ||= []).push(item);
      });
    return result;
  }, [products, search]);

  const selectedProducts = useMemo(
    () => products.filter(item => draft.sku_ids.includes(item.sku_id)),
    [products, draft.sku_ids]
  );

  // Категории нужны только для группировки "по категории" — тянуть список
  // брендов/категорий на каждое открытие мастера незачем.
  useEffect(() => {
    if (groupBy !== 'per_category' || !editor || categoryFilters) return;
    let alive = true;
    setCategoryFiltersLoading(true);
    request(`/api/v1/ozon-ads/${accountId}/generator/product-filters`)
      .then(result => { if (alive) setCategoryFilters(result.categories || []); })
      .catch(() => { if (alive) setCategoryFilters([]); })
      .finally(() => { if (alive) setCategoryFiltersLoading(false); });
    return () => { alive = false; };
  }, [groupBy, editor, categoryFilters, request, accountId]);

  // Как выбранный пул режется на будущие автоматизации. По модели — рекомендуемо
  // (внутри модели трафик перетекает между вариантами), по артикулу, категории
  // или ценовому диапазону — как в «Генераторе РК», когда группы нужно резать иначе.
  const groups = useMemo(() => {
    if (!selectedProducts.length) return [];
    if (groupBy === 'per_sku') {
      return selectedProducts.map(product => ({
        key: String(product.sku_id),
        name: product.offer_id || product.name || String(product.sku_id),
        sku_ids: [product.sku_id],
      }));
    }
    if (groupBy === 'per_category') {
      const nameByKey = new Map((categoryFilters || [])
        .map(item => [`${item.category_id}:${item.type_id ?? '-'}`, item.name]));
      const byCategory = new Map();
      selectedProducts.forEach(product => {
        const key = product.category_id != null
          ? `${product.category_id}:${product.type_id ?? '-'}` : 'none';
        if (!byCategory.has(key)) byCategory.set(key, []);
        byCategory.get(key).push(product);
      });
      return [...byCategory.entries()].map(([key, items]) => ({
        key: `category:${key}`,
        name: key === 'none' ? 'Без категории' : (nameByKey.get(key) || 'Категория'),
        sku_ids: items.map(item => item.sku_id),
      }));
    }
    if (groupBy === 'per_price_range') {
      const priceBySku = Object.fromEntries(
        (priceContext?.items || []).map(item => [String(item.sku_id), item.price]));
      const byRange = new Map();
      selectedProducts.forEach(product => {
        const price = priceBySku[String(product.sku_id)];
        const range = price == null ? null : PRICE_RANGES.find(item =>
          price >= item.min_price && (item.max_price == null || price < item.max_price));
        const label = range ? range.label : 'Цена неизвестна';
        if (!byRange.has(label)) byRange.set(label, []);
        byRange.get(label).push(product);
      });
      return [...byRange.entries()].map(([label, items]) => ({
        key: `price:${label}`, name: label, sku_ids: items.map(item => item.sku_id),
      }));
    }
    const byModel = new Map();
    selectedProducts.forEach(product => {
      const key = product.model_name || 'Без модели';
      if (!byModel.has(key)) byModel.set(key, []);
      byModel.get(key).push(product);
    });
    return [...byModel.entries()].map(([name, items]) => ({
      key: name, name, sku_ids: items.map(item => item.sku_id),
    }));
  }, [selectedProducts, groupBy, categoryFilters, priceContext]);

  // Смена группировки меняет сами ключи групп — старые переопределения и
  // галочки "создавать/не создавать" от прежней разбивки только путают.
  const groupByRef = useRef(groupBy);
  useEffect(() => {
    if (groupByRef.current === groupBy) return;
    groupByRef.current = groupBy;
    setGroupOverrides({});
    setGroupToggles({});
    setOpenGroupKey(null);
  }, [groupBy]);

  // Если товары группы уже целиком входят в существующую автоматизацию —
  // создавать новую не нужно, это будет дубль управления одними и теми же
  // РК. Частичное совпадение — не дубль, только предупреждение.
  const groupCoverage = useMemo(() => {
    const existing = automations.map(item => ({
      id: item.id, name: item.name, skuSet: new Set(item.sku_ids || []),
    }));
    const result = {};
    groups.forEach(group => {
      let best = null;
      existing.forEach(automation => {
        const overlap = group.sku_ids.filter(id => automation.skuSet.has(id)).length;
        if (overlap > 0 && (!best || overlap > best.overlap)) {
          best = { name: automation.name, overlap, total: group.sku_ids.length };
        }
      });
      if (best) result[group.key] = best;
    });
    return result;
  }, [groups, automations]);

  const isGroupSelected = key => {
    if (Object.prototype.hasOwnProperty.call(groupToggles, key)) return groupToggles[key];
    // Пересечение с существующей автоматизацией — только предупреждение
    // (см. groupCoverage и бейдж на карточке группы). Молча снимать галочку
    // нельзя: выбор части модели (например, без неликвида) почти всегда
    // пересекается с более широкой старой автоматизацией на ту же модель —
    // это не дубль, а осознанное решение пользователя, за него не решаем.
    return true;
  };
  const setGroupToggle = (key, value) => setGroupToggles(current => ({ ...current, [key]: value }));
  const selectedGroups = useMemo(
    () => groups.filter(group => isGroupSelected(group.key)),
    [groups, groupToggles, groupCoverage]
  );

  const missingProducts = useMemo(() => {
    const selectedSkuSet = new Set(selectedGroups.flatMap(group => group.sku_ids));
    return selectedProducts.filter(item => selectedSkuSet.has(item.sku_id) && !item.control_targets?.length);
  }, [selectedProducts, selectedGroups]);

  // Текущая цена и рентабельность нужны на шаге ограничителей: там задаётся
  // коридор и там же они показываются справочно. Раньше этого шага запрос не
  // уходит, поэтому открытие мастера само по себе OZON не дёргает.
  const limitsVisible = (editor === 'MANUAL' ? openSection >= 4 : step >= 4) || groupBy === 'per_price_range';
  const priceSkuKey = limitsVisible ? draft.sku_ids.join(',') : '';
  useEffect(() => {
    if (!editor || !priceSkuKey) return undefined;
    let alive = true;
    setPriceError('');
    getOzonAutomationLimitsContext({ request, accountId, skuIds: priceSkuKey.split(',') })
      .then(data => { if (alive) setPriceContext(data); })
      .catch(err => { if (alive) setPriceError(readError(err)); });
    return () => { alive = false; };
  }, [editor, priceSkuKey, request, accountId]);

  // Проценты превращаются в рубли здесь и сейчас, а не хранятся процентами.
  // Коридор, который каждый раз пересчитывается от текущей цены, уезжает вниз
  // вслед за каждым снижением и перестаёт быть границей — поэтому он
  // привязывается к цене на момент настройки, и пользователь видит рубли.
  const priceLocked = useMemo(() => {
    const owners = (priceContext?.items || [])
      .filter(item => item.price_owner && selectedProducts.some(p => String(p.sku_id) === String(item.sku_id)));
    return { count: owners.length, all: owners.length > 0 && owners.length >= selectedProducts.length,
      tools: [...new Set(owners.map(item => item.price_owner))] };
  }, [priceContext, selectedProducts]);
  const applyPriceCorridor = (downPct, upPct) => {
    const down = Number(downPct);
    const up = Number(upPct);
    if (!Number.isFinite(down) || !Number.isFinite(up)) return 0;
    const bySku = Object.fromEntries(
      (priceContext?.items || []).map(item => [String(item.sku_id), item]));
    const nextPerSku = { ...(draft.constraints.per_sku || {}) };
    let applied = 0;
    let single = null;
    selectedProducts.forEach(product => {
      const price = bySku[String(product.sku_id)]?.price;
      // Ценой ведёт РРЦ (жёстко или по конкурентам), медиана или расписание —
      // границы цены этому товару не ставятся.
      if (price == null || bySku[String(product.sku_id)]?.price_owner) return;
      const min = Math.max(1, Math.round(price * (1 - down / 100)));
      const max = Math.max(min, Math.round(price * (1 + up / 100)));
      applied += 1;
      single = { min, max };
      nextPerSku[product.sku_id] = {
        ...(nextPerSku[product.sku_id] || {}), min_price: min, max_price: max };
    });
    if (!applied) return 0;
    // Один товар — персональные границы прятать незачем, пишем в общие.
    if (selectedProducts.length === 1 && single) {
      patch({ constraints: { ...draft.constraints,
        min_price: single.min, max_price: single.max } });
    } else {
      patch({ constraints: { ...draft.constraints, per_sku: nextPerSku } });
    }
    return applied;
  };
  const visibleIds = useMemo(
    () => Object.values(grouped).flat().map(item => item.sku_id), [grouped]
  );
  // Products that already belong to another group (the group being edited does not count).
  const takenByOthers = useMemo(() => new Set(automations
    .filter(item => item.id !== editingId && item.status !== 'ARCHIVED')
    .flatMap(item => (item.sku_ids || []).map(Number))), [automations, editingId]);
  const freeVisibleIds = visibleIds.filter(id => !takenByOthers.has(Number(id)));

  const patch = values => setDraft(current => ({
    ...current, ...values,
    control_targets: (values.control_targets || current.control_targets || [])
      .filter(target => (values.sku_ids || current.sku_ids).includes(target.sku_id)),
  }));
  const patchSection = (section, values) => setDraft(current => ({
    ...current, [section]: { ...current[section], ...values },
  }));
  const toggleSku = item => patch({
    sku_ids: draft.sku_ids.includes(item.sku_id)
      ? draft.sku_ids.filter(value => value !== item.sku_id) : [...draft.sku_ids, item.sku_id],
  });
  const toggleModel = items => {
    const ids = items.map(item => item.sku_id);
    patch({ sku_ids: ids.every(id => draft.sku_ids.includes(id))
      ? draft.sku_ids.filter(id => !ids.includes(id)) : [...new Set([...draft.sku_ids, ...ids])] });
  };

  const objectiveGaps = () => {
    if (!spec) return [];
    const gaps = [];
    (spec.required_objective || []).forEach(name => {
      const value = draft.objective[name];
      if (value === '' || value == null) gaps.push(name === 'target_date' ? 'срок' : 'целевое значение');
    });
    return [...new Set(gaps)];
  };

  const canContinue = () => {
    if (step === 1) {
      if (!draft.sku_ids.length) return false;
      if (draft.status === 'DRAFT') return true;
      // An AUTO group is defined by its products: campaigns are found for it,
      // so there is nothing to pick. A fixed list is only required when the
      // user chose to fix it and the bid lever is in play.
      if (draft.target_mode === 'AUTO') return true;
      return !draft.permissions.bid || draft.control_targets.length > 0;
    }
    if (step === 2) return Boolean(draft.strategy_type);
    if (step === 3) return objectiveGaps().length === 0;
    if (step === 5) return selectedGroups.length > 0;
    return true;
  };

  const wireObjective = objective => ({
    ...objective,
    target_value: objective.target_value === '' ? null : Number(objective.target_value),
  });
  const wireConstraints = constraints => ({
    ...constraintsForWire(constraints),
    per_sku: Object.fromEntries(Object.entries(constraints.per_sku || {})
      .map(([sku, row]) => [sku, Object.fromEntries(Object.entries(row)
        .filter(([, value]) => value !== '' && value != null)
        .map(([key, value]) => [key,
          FRACTION_FIELDS.includes(key) ? toFraction(value) : Number(value)]))])
      .filter(([, row]) => Object.keys(row).length)),
    max_bid: constraints.max_bid === '' ? null : Number(constraints.max_bid),
    experiment_budget: constraints.experiment_budget === '' ? null : Number(constraints.experiment_budget),
    price_change_limit_pct: Number(constraints.price_change_limit_pct),
    bid_change_limit_pct: Number(constraints.bid_change_limit_pct),
  });
  const wireEvaluation = evaluation => ({
    ...evaluation,
    meaningful_change_pct: Number(evaluation.meaningful_change_pct),
    min_days: Number(evaluation.min_days), max_days: Number(evaluation.max_days),
  });

  const save = async () => {
    setSaving(true);
    setError('');
    try {
      const payload = {
        ...draft,
        name: draft.name.trim() || `${strategyLabel(draft.strategy_type)} · ${selectedProducts[0]?.model_name || selectedProducts[0]?.name}`,
        objective: wireObjective(draft.objective),
        constraints: wireConstraints(draft.constraints),
        evaluation: wireEvaluation(draft.evaluation),
      };
      if (editingId) {
        await updateOzonAutomation({ request, accountId, automationId: editingId, automation: payload });
      } else {
        await createOzonAutomation({ request, accountId, automation: payload });
      }
      setEditor(null);
      setEditingId(null);
      await load();
    } catch (err) { setError(readError(err)); }
    finally { setSaving(false); }
  };

  // Групповые переопределения: только стратегия и ключевые ограничители —
  // остального в мастере массовой настройки достаточно менять для всех сразу.
  const setGroupStrategy = (key, strategyType) => setGroupOverrides(current => {
    const row = { ...(current[key] || {}) };
    if (!strategyType) delete row.strategy_type; else row.strategy_type = strategyType;
    const next = { ...current };
    if (Object.keys(row).length) next[key] = row; else delete next[key];
    return next;
  });
  const setGroupConstraint = (key, field, value) => setGroupOverrides(current => {
    const row = { ...(current[key] || {}) };
    const constraints = { ...(row.constraints || {}) };
    if (value === '' || value == null) delete constraints[field]; else constraints[field] = Number(value);
    if (Object.keys(constraints).length) row.constraints = constraints; else delete row.constraints;
    const next = { ...current };
    if (Object.keys(row).length) next[key] = row; else delete next[key];
    return next;
  });
  const clearGroupOverride = key => setGroupOverrides(current => {
    const next = { ...current };
    delete next[key];
    return next;
  });

  // Пачками по 150: один запрос на весь магазин — это одна огромная транзакция
  // и риск таймаута на прокси. Чанки идут последовательно, чтобы был виден
  // прогресс и чтобы отказ одного чанка не терял уже созданные группы.
  const BATCH_CHUNK_SIZE = 150;
  const saveBatch = async () => {
    setBatchSaving(true);
    setBatchProgress('');
    setError('');
    try {
      const defaults = {
        strategy_type: draft.strategy_type, status: draft.status, execution_mode: draft.execution_mode,
        target_mode: 'AUTO', source: 'WIZARD',
        positioning: draft.positioning,
        objective: wireObjective(draft.objective),
        constraints: wireConstraints(draft.constraints),
        permissions: draft.permissions,
        evaluation: wireEvaluation(draft.evaluation),
      };
      const groupsPayload = selectedGroups.map(group => {
        const override = groupOverrides[group.key] || {};
        // per_sku копился на весь выбранный пул мастера (шаг 4), а не на одну
        // группу — бэкенд отказывает автоматизации, если в её constraints
        // упомянут SKU не из её же sku_ids. Обрезаем список до SKU именно
        // этой группы перед отправкой, иначе отказывают все группы разом.
        const groupSkuSet = new Set(group.sku_ids.map(String));
        const scopedPerSku = Object.fromEntries(
          Object.entries(defaults.constraints.per_sku || {}).filter(([sku]) => groupSkuSet.has(sku)));
        const overrides = {
          constraints: { ...defaults.constraints, per_sku: scopedPerSku, ...(override.constraints || {}) },
        };
        if (override.strategy_type) overrides.strategy_type = override.strategy_type;
        return {
          name: `${strategyLabel(override.strategy_type || draft.strategy_type)} · ${group.name}`,
          sku_ids: group.sku_ids,
          overrides,
        };
      });
      const created = [];
      const errors = [];
      let chunkFailed = false;
      for (let offset = 0; offset < groupsPayload.length; offset += BATCH_CHUNK_SIZE) {
        const chunk = groupsPayload.slice(offset, offset + BATCH_CHUNK_SIZE);
        setBatchProgress(`${Math.min(offset + BATCH_CHUNK_SIZE, groupsPayload.length)} из ${groupsPayload.length}`);
        try {
          const result = await createOzonAutomationsBatch({ request, accountId, defaults, groups: chunk });
          created.push(...(result.created || []));
          errors.push(...(result.errors || []));
        } catch (err) {
          // Уже созданные пачки — реальные автоматизации в базе, их нельзя
          // потерять из вида из-за отказа следующей пачки; поэтому список
          // обновляется в любом случае, а не только при полном успехе.
          errors.push({ name: `пачка ${offset + 1}–${offset + chunk.length}`, error: readError(err) });
          chunkFailed = true;
          break;
        }
      }
      if (errors.length) {
        setError(`Создано ${created.length} из ${groupsPayload.length}${chunkFailed ? ' — прервано ошибкой' : ''}. Не удалось: ${
          errors.map(item => `${item.name} — ${item.error}`).join('; ')}`);
      }
      if (created.length) { setEditor(null); setEditingId(null); }
      await load();
    } catch (err) { setError(readError(err)); }
    finally { setBatchSaving(false); setBatchProgress(''); }
  };

  // Товары без единой РК не пропускают ставку никуда — прежде чем создавать
  // автоматизации, стоит закрыть эту дыру тем же способом, что и «Генератор РК»:
  // минимальная ставка, Поиск + Поиск и рекомендации.
  const buildCampaignPlan = async () => {
    setCampaignPlanLoading(true);
    setCampaignPlanError('');
    try {
      const planBody = {
        grouping: groupBy,
        campaign_types: [
          { placement: 'PLACEMENT_TOP_PROMOTION', strategy: 'NO_AUTO_STRATEGY' },
          { placement: 'PLACEMENT_SEARCH_AND_CATEGORY', strategy: 'NO_AUTO_STRATEGY' },
        ],
        bid_strategy: { type: 'min', add_value: 0, multiplier: 1 },
        filter_skus: missingProducts.map(item => item.sku_id),
        ...(groupBy === 'per_price_range' ? { price_ranges: PRICE_RANGES } : {}),
      };
      const result = await request(`/api/v1/ozon-ads/${accountId}/generator/plan`, {
        method: 'POST',
        body: JSON.stringify(planBody),
      });
      setCampaignPlan(result);
      setCampaignPlanBody(planBody);
    } catch (err) { setCampaignPlanError(readError(err)); }
    finally { setCampaignPlanLoading(false); }
  };

  const pollCampaignApplyJob = async jobId => {
    try {
      const status = await request(`/api/v1/ozon-ads/${accountId}/generator/apply/status/${jobId}`);
      if (status.status === 'done') {
        // Очередь кладёт итог в result.results (созданные РК и ошибки).
        setCampaignApplyResult(status.result?.results || status.result || {});
        setCampaignApplyLoading(false);
        await load();
        return;
      }
      if (status.status === 'failed') {
        setCampaignPlanError(status.result?.error || 'Не удалось создать РК');
        setCampaignApplyLoading(false);
        return;
      }
      setTimeout(() => pollCampaignApplyJob(jobId), 2500);
    } catch (err) { setCampaignPlanError(readError(err)); setCampaignApplyLoading(false); }
  };

  const applyCampaignPlan = async () => {
    if (!campaignPlan || !campaignPlanBody) return;
    setCampaignApplyLoading(true);
    setCampaignPlanError('');
    try {
      // strategy 2 — «только создание новых»: существующие РК не трогаем.
      // У Ozon у товарной РК только недельный бюджет: минимум 2 000 ₽ на товар.
      const result = await request(`/api/v1/ozon-ads/${accountId}/generator/apply`, {
        method: 'POST',
        body: JSON.stringify({
          strategy: 2,
          plan: campaignPlanBody,
          weekly_budget_per_sku_rubles: 2000,
        }),
      });
      await pollCampaignApplyJob(result.job_id);
    } catch (err) { setCampaignPlanError(readError(err)); setCampaignApplyLoading(false); }
  };

  const changeStatus = async item => {
    setError('');
    try {
      await setOzonAutomationStatus({
        request, accountId, automationId: item.id,
        status: item.status === 'ACTIVE' ? 'PAUSED' : 'ACTIVE',
      });
      await load();
    } catch (err) { setError(readError(err)); }
  };

  // The same removal as in the table: a group that changed something goes to
  // the archive with its journal, an empty one is removed at once.
  const removeGroup = async item => {
    if (!window.confirm(`Удалить «${item.name}»? Если группа уже меняла ставки или цены, она уйдёт в архив `
      + 'вместе с журналом (вкладка «Таблица» → «Архив»). Перед этим она вернёт то, что сделала сама.')) return;
    setError('');
    try {
      const result = await bulkOzonAutomations({ request, accountId, ids: [item.id], action: 'archive' });
      if (result.warnings?.length) setError(result.warnings.map(entry => entry.warning).join('; '));
      if (expandedId === item.id) setExpandedId(null);
      await load();
    } catch (err) { setError(readError(err)); }
  };

  if (loading) return <div className='bidder__loading'>Загрузка автоматизаций…</div>;

  const manual = editor === 'MANUAL';
  // Свёрнутый раздел обязан отвечать на вопрос «что я там уже задал», иначе
  // сворачивание просто прячет работу.
  const sectionSummary = {
    1: draft.sku_ids.length
      ? `${draft.sku_ids.length} тов. · ${draft.target_mode === 'AUTO'
        ? 'все кампании' : `${draft.control_targets.length} пар РК–товар`}`
      : 'товары не выбраны',
    2: strategyLabel(draft.strategy_type) || 'цель не выбрана',
    3: objectiveGaps().length ? `осталось заполнить: ${objectiveGaps().join(', ')}` : 'заполнено',
    4: [
      Object.entries(draft.permissions).filter(([, on]) => on)
        .map(([key]) => ({ bid: 'ставка', price: 'цена' }[key]))
        .filter(Boolean).join(', ') || 'рычаги выключены',
      { AUTO: 'автономно', CONTROLLED: 'с подтверждением', OBSERVE: 'наблюдение' }[draft.execution_mode],
      draft.permissions.jev ? 'курс: Jev' : 'курс: скрипт',
    ].join(' · '),
    5: draft.name.trim() || 'название по умолчанию',
  };
  const hiddenSection = number => skipGoal && (number === 2 || number === 3);
  const sectionHead = (number, title) => manual && !hiddenSection(number) && <button type='button'
    className={`oz-auto__section-head${openSection === number ? ' is-open' : ''}`}
    aria-expanded={openSection === number}
    onClick={() => setOpenSection(value => (value === number ? 0 : number))}>
    <strong>{title}</strong><span>{sectionSummary[number]}</span>
    <em>{openSection === number ? '−' : '+'}</em>
  </button>;
  const showSection = number => !hiddenSection(number) && (manual ? openSection === number : step === number);
  return (
    <div className='oz-auto'>
      <header className='oz-auto__header'>
        <div><h2>Автоматизации OZON</h2><p>Цель задаёт пользователь. Статистика выбранных товаров, проверки ставок и история результатов.</p></div>
        {!editor && <div className='oz-auto__create'>
          <div className='oz-auto__tabs' role='tablist' aria-label='Вид списка'>
            {[['cards', 'Карточки'], ['table', 'Таблица']].map(([value, label]) => <button type='button' key={value}
              role='tab' aria-selected={listView === value} className={listView === value ? 'is-active' : ''}
              onClick={() => changeListView(value)}>{label}</button>)}
          </div>
          <button type='button' className='oz-auto__primary' onClick={() => openEditor('WIZARD')}>+ Через мастер</button>
          <button type='button' onClick={() => openEditor('MANUAL')}>Настроить вручную</button>
        </div>}
      </header>
      {error && <div className='bidder__error'>{error}</div>}

      {editor ? <section className='oz-auto__editor'>
        <div className='oz-auto__editor-head'>
          <div><small>{editor === 'WIZARD' ? `Шаг ${stepPosition} из ${wizardSteps.length}` : 'Все настройки на одной странице'}</small>
            <h3>{editor === 'WIZARD' ? 'Мастер массовой настройки автоматизации'
              : editingId ? 'Настройки автоматизации' : 'Ручная настройка автоматизации'}</h3></div>
          <button type='button' onClick={() => { setEditor(null); setEditingId(null); }}>Закрыть</button>
        </div>
        {editor === 'WIZARD' && <div className='oz-auto__progress'><i style={{ width: `${(stepPosition / wizardSteps.length) * 100}%` }} /></div>}

        {sectionHead(1, 'Товары и кампании')}
        {showSection(1) && <div className='oz-auto__section'>
          {!manual && <div className='oz-auto__field'>
            <span>Как группировать товары для автоматизации<Hint text='Определяет, на какие товары настраиваем: одна группа получит одну автоматизацию.' /></span>
            <div className='oz-auto__grouping-choice'>
              <label className={groupBy === 'per_model' ? 'is-selected' : ''}>
                <input type='radio' name='groupBy' checked={groupBy === 'per_model'} onChange={() => setGroupBy('per_model')} />
                <span><strong>1 группа = 1 модель</strong>
                  <small>Рекомендуем: внутри модели трафик перетекает между вариантами,
                    и результат правильно оценивать по всей модели сразу.</small></span>
              </label>
              <label className={groupBy === 'per_sku' ? 'is-selected' : ''}>
                <input type='radio' name='groupBy' checked={groupBy === 'per_sku'} onChange={() => setGroupBy('per_sku')} />
                <span><strong>1 группа = 1 артикул</strong>
                  <small>Как в «Генераторе РК» — каждый товар настраивается и оценивается отдельно.</small></span>
              </label>
              <label className={groupBy === 'per_category' ? 'is-selected' : ''}>
                <input type='radio' name='groupBy' checked={groupBy === 'per_category'} onChange={() => setGroupBy('per_category')} />
                <span><strong>По категории</strong>
                  <small>Один товарный тип каталога — одна группа.
                    {groupBy === 'per_category' && categoryFiltersLoading ? ' Загружаем категории…' : ''}</small></span>
              </label>
              <label className={groupBy === 'per_price_range' ? 'is-selected' : ''}>
                <input type='radio' name='groupBy' checked={groupBy === 'per_price_range'} onChange={() => setGroupBy('per_price_range')} />
                <span><strong>По ценовому диапазону</strong>
                  <small>Границы как в «Генераторе РК»: {PRICE_RANGES.map(item => item.label).join(', ')}.
                    {groupBy === 'per_price_range' && !priceContext && !priceError ? ' Получаем цены из кабинета OZON…' : ''}</small></span>
              </label>
            </div>
          </div>}
          <h4>Какими товарами управляем?{manual && <Hint text='Связанные варианты можно выбрать одной моделью. Результат оценивается по всей выбранной группе.' />}</h4>
          {!manual && <p>Связанные варианты можно выбрать одной моделью. Результат будет оцениваться по всей выбранной группе.</p>}
          <label className='oz-auto__field'><span>Или добавить товары из РК</span><select value='' onChange={e => {
            const ids = products.filter(product => product.control_targets?.some(target => target.campaign_id === e.target.value)).map(product => product.sku_id);
            patch({ sku_ids: [...new Set([...draft.sku_ids, ...ids])] });
          }}><option value=''>Выберите кампанию</option>{[...new Map(products.flatMap(product => product.control_targets || []).map(target => [target.campaign_id, target])).values()].map(target => <option key={target.campaign_id} value={target.campaign_id}>{target.campaign_name}</option>)}</select></label>
          <input className='oz-auto__search' value={search} onChange={e => setSearch(e.target.value)} placeholder='Название, артикул, SKU или модель' />
          <div className='oz-auto__bulk-select'>
            <button type='button' disabled={!freeVisibleIds.length} onClick={() => patch({
              sku_ids: [...new Set([...draft.sku_ids, ...freeVisibleIds])],
            })}>Выбрать все, что не в других группах{freeVisibleIds.length ? ` (${freeVisibleIds.length})` : ''}</button>
            <button type='button' disabled={!visibleIds.some(id => draft.sku_ids.includes(id))} onClick={() => patch({
              sku_ids: draft.sku_ids.filter(id => !visibleIds.includes(id)),
            })}>Снять выбор со всех{search ? ' найденных' : ''}</button>
            <button type='button' disabled={!draft.sku_ids.length}
              onClick={() => patch({ sku_ids: [], managed_sku_ids: [] })}>Очистить выбор</button>
          </div>
          <div className='oz-auto__products'>
            {Object.entries(grouped).map(([model, items]) => <div className='oz-auto__model' key={model}>
              <label className='oz-auto__model-title'><input type='checkbox'
                checked={items.every(item => draft.sku_ids.includes(item.sku_id))}
                onChange={() => toggleModel(items)} /> <strong>{model}</strong><span>{items.length} тов.</span></label>
              {items.map(item => <label className='oz-auto__product' key={item.sku_id}>
                <input type='checkbox' checked={draft.sku_ids.includes(item.sku_id)} onChange={() => toggleSku(item)} />
                {item.image ? <img src={item.image} alt='' /> : <span className='oz-auto__noimage' />}
                <span><strong>{item.name}</strong><small>{item.offer_id || 'Без артикула'} · SKU {item.sku_id}</small>
                  <small className={item.control_targets?.some(target => target.available) ? 'oz-auto__rk-ok' : 'oz-auto__rk-none'}>
                    {item.control_targets?.length ? `РК: ${item.control_targets.length}; доступны: ${item.control_targets.filter(target => target.available).length}` : 'РК не найдены — можно включить в общую статистику'}
                  </small>
                  <StockBadge fbo={item.stock_fbo} fbs={item.stock_fbs} /></span>
                <em>{item.cogs == null ? 'Себестоимость не указана' : `${item.cogs.toLocaleString('ru-RU')} ₽`}</em>
              </label>)}
            </div>)}
          </div>
          <div className='oz-auto__selected'>Выбрано: <strong>{draft.sku_ids.length}</strong></div>
          {!manual && draft.sku_ids.length > 0 && (() => {
            const coveredCount = groups.filter(group => {
              const coverage = groupCoverage[group.key];
              return coverage && coverage.overlap === coverage.total;
            }).length;
            return <p className='oz-auto__hint'>
              Будет создано групп: <strong>{groups.length}</strong> — каждая станет
              отдельной автоматизацией со своими кампаниями (режим «все кампании товара»).
              {coveredCount > 0 && ` Уже настроено: ${coveredCount} — на шаге «Группы» они помечены; снимите галочку, если не хотите дублировать управление.`}
            </p>;
          })()}
          {manual && selectedProducts.length > 0 && <div className='oz-auto__roles'>
            <h5>Какими кампаниями управлять</h5>
            <label className='oz-auto__target-mode'>
              <input type='radio' name='target_mode' checked={draft.target_mode === 'AUTO'}
                onChange={() => patch({ target_mode: 'AUTO', control_targets: [] })} />
              <span><strong>Все кампании выбранных товаров</strong>
                <small>Группу задают товары. Любая кампания, которая рекламирует
                  выбранный товар, попадает в эту группу — включая те, что вы создадите
                  позже. Ничего дополнительно отмечать не нужно.</small></span>
            </label>
            <label className='oz-auto__target-mode'>
              <input type='radio' name='target_mode' checked={draft.target_mode !== 'AUTO'}
                onChange={() => patch({ target_mode: 'MANUAL' })} />
              <span><strong>Только отмеченные пары РК–товар</strong>
                <small>Список фиксируется: новые кампании в группу не войдут, пока вы
                  не отметите их здесь сами.</small></span>
            </label>
            {draft.target_mode === 'AUTO' && (() => {
              const found = selectedProducts.reduce(
                (sum, product) => sum + (product.control_targets?.length || 0), 0);
              return <p className={found ? 'oz-auto__rk-ok' : 'oz-auto__rk-none'}>
                {found
                  ? `Сейчас в группу попадает кампаний: ${found}. Новые добавятся сами.`
                  : 'Рекламные кампании по этим товарам пока не найдены. Группу можно '
                    + 'создать сейчас — кампании подхватятся, как только появятся, но до '
                    + 'этого ставками управлять будет нечем.'}</p>;
            })()}
            {draft.target_mode !== 'AUTO' && <>
            <p>Все выбранные товары участвуют в общей статистике. Галочка ниже разрешает воздействовать только на указанную пару РК–товар.</p>
            {selectedProducts.map(product => <div key={product.sku_id}>
              <strong>{product.offer_id || product.sku_id}</strong>
              {!product.control_targets?.length && <p>Рекламные кампании не найдены. Сохраните черновик и подключите РК после создания и синхронизации.</p>}
              {(product.control_targets || []).map(target => {
                const checked = draft.control_targets.some(value => value.campaign_id === target.campaign_id && value.sku_id === target.sku_id);
                return <label className='oz-auto__target-choice' key={target.key}>
                  <input type='checkbox' checked={checked} disabled={!target.available && !checked} onChange={e => patch({
                    control_targets: e.target.checked ? [...draft.control_targets, { lever: 'BID', campaign_id: target.campaign_id, sku_id: target.sku_id, placement: target.placement }]
                      : draft.control_targets.filter(value => !(value.campaign_id === target.campaign_id && value.sku_id === target.sku_id)),
                  })} />
                  <span><strong>{target.campaign_name}</strong><small>{target.placement_label} · {target.bid == null ? 'Ставка неизвестна' : `${target.bid} ₽`}</small>
                    {!target.available && <small>{target.reasons.join('. ')}</small>}</span>
                </label>;
              })}
            </div>)}
            <p>Выбрано объектов управления: {draft.control_targets.length}</p>
            </>}
            <label className='oz-auto__draft-toggle'><input type='checkbox' checked={draft.status === 'DRAFT'} onChange={e => patch({ status: e.target.checked ? 'DRAFT' : 'ACTIVE' })} /> Сохранить черновик без запуска</label>
          </div>}
        </div>}

        {sectionHead(2, 'Цель')}
        {showSection(2) && <div className='oz-auto__section'>
          <h4>Чего хотите добиться?</h4>
          <div className='oz-auto__strategies'>{strategies.map(strategy =>
            <button type='button' key={strategy.key}
              disabled={strategy.available === false}
              title={strategy.unavailable_reason
                || (manual ? `${strategy.description}
Рычаги: ${strategy.levers.map(lever => lever.label).join(' → ')}` : '')}
              className={draft.strategy_type === strategy.key ? 'is-selected' : ''}
              onClick={() => patch({
                strategy_type: strategy.key,
                permissions: {
                  ...Object.fromEntries(strategy.levers.map(
                    (lever, index) => [lever.lever.toLowerCase(), index === 0])),
                  budget: Boolean(draft.permissions.budget),
                  jev: Boolean(draft.permissions.jev),
                },
              })}>
              {/* Тринадцать карточек по четыре строки — это стена, но сжаты
                  они только в ручном режиме: мастер принадлежит другой работе
                  и остаётся как был. */}
              <strong>{strategy.title}</strong>
              {manual ? <>
                <small>{strategy.goal_label}</small>
                {draft.strategy_type === strategy.key && <>
                  <small>{strategy.description}</small>
                  <small>Рычаги: {strategy.levers.map(lever => lever.label).join(' → ')}</small>
                </>}
              </> : <>
                <small>{strategy.description}</small>
                <small>Цель измеряется в: {strategy.goal_label}</small>
                <small>Рычаги: {strategy.levers.map(lever => lever.label).join(' → ')}</small>
              </>}
              {strategy.available === false
                && <small className='oz-auto__unavailable'>{strategy.unavailable_reason}</small>}
            </button>)}</div>
          {spec && <p className='oz-auto__muted'>
            {spec.requires_economics ? ' Цель требует заполненной себестоимости и подтверждённой экономики.' : ''}
            {spec.requires_stock ? ' Цель требует известного остатка.' : ''}
            {spec.requires_coinvest ? ' Цель требует подтверждённого соинвеста.' : ''}
          </p>}
        </div>}

        {sectionHead(3, 'Параметры цели')}
        {showSection(3) && <div className='oz-auto__section'>
          <h4>Уточним цель</h4>
          <ObjectiveFields draft={draft} patchSection={patchSection} spec={spec} />
          {objectiveGaps().length > 0 && <div className='oz-auto__notice'>
            Осталось заполнить: {objectiveGaps().join(', ')}.</div>}
        </div>}

        {sectionHead(4, 'Рычаги и ограничители')}
        {showSection(4) && <div className='oz-auto__section'>
          <h4>Что системе разрешено?{manual && <Hint text='Отмечены только те рычаги, которые использует выбранная цель. Порядок — приоритет проверки.' />}</h4>
          {!manual && <p>Отмечены только те рычаги, которые использует выбранная цель. Порядок — приоритет проверки.</p>}
          <div className='oz-auto__permissions'>
            {strategyLevers.map(lever => {
              const key = lever.lever.toLowerCase();
              // Цену всех выбранных товаров ведёт другой инструмент — рычаг цены не выбирается.
              const locked = key === 'price' && priceLocked.all;
              return <label key={key}>
                <input type='checkbox' checked={Boolean(draft.permissions[key]) && !locked} disabled={locked}
                  onChange={e => patchSection('permissions', { [key]: e.target.checked })} />
                <span><strong>{lever.label}</strong>
                  <small>{LEVER_HINTS[lever.lever]}</small>
                  <small>{lever.note}</small>
                  {key === 'price' && priceLocked.count > 0 && <small className='oz-auto__unavailable'>
                    {locked ? 'Цену всех выбранных товаров ведёт другой инструмент: ' : `Цену ${priceLocked.count} из ${selectedProducts.length} товаров ведёт другой инструмент: `}
                    {priceLocked.tools.join(', ')}. Биддер и контроль соинвеста их цену не меняют.</small>}</span>
              </label>;
            })}
          </div>
          <div className='oz-auto__permissions'>
            <label>
              <input type='checkbox' checked={Boolean(draft.permissions.budget)}
                onChange={e => patchSection('permissions', { budget: e.target.checked })} />
              <span><strong>Бюджет РК</strong>
                <small>Недельный бюджет кампании = недельный темп выручки × плановый общий ДРР × (1 + рост). Следует за продажами вверх и вниз.</small>
                <small>Работает только для кампаний, где все товары входят в эту автоматизацию (рекомендуется одна РК на один артикул). Резкий провал или скачок выручки применяется после вашего согласования.</small></span>
            </label>
          </div>
          {!strategyLevers.some(lever => draft.permissions[lever.lever.toLowerCase()])
            && <div className='oz-auto__notice'>Без разрешённого рычага автоматизация сможет только наблюдать.</div>}
          <label className='oz-auto__field'><span>Кто принимает решения</span>
            <select value={draft.execution_mode} onChange={e => patch({ execution_mode: e.target.value })}>
              {MODES.map(([value, label]) => <option key={value} value={value}>{label}</option>)}
            </select>
            <small>{MODE_NOTE[draft.execution_mode]}</small></label>
          <label className='oz-auto__field'><span>Тактика</span>
            <select value={draft.permissions.jev ? 'jev' : 'script'}
              onChange={e => patchSection('permissions', { jev: e.target.value === 'jev' })}>
              <option value='script'>Скрипт — шаги по своим правилам</option>
              <option value='jev'>Jev — скрипт исполняет курс ИИ</option>
            </select>
            <small>{draft.permissions.jev
              ? 'Раз в сутки Jev задаёт курс (что двигать, куда и насколько смело), скрипт делает шаги в его рамках и в рамках ограничителей. Если Jev недоступен или выключен администратором — скрипт работает сам.'
              : 'Скрипт сам выбирает шаги ставкой и ценой. Jev при этом может давать курс в журнал, но ему не подчиняются.'}</small></label>

          <div className='oz-auto__fields'>
            {draft.permissions.bid && <>
              <NumberField label='Максимальная ставка, ₽' hint='Абсолютный предел ставки для любой проверки. Без него отправка изменений недоступна.' value={draft.constraints.max_bid ?? ''} onChange={value => patchSection('constraints', { max_bid: value })} />
              <NumberField label='Макс. шаг ставки, %' hint='На сколько процентов ставка может измениться за одно действие.' value={draft.constraints.bid_change_limit_pct} onChange={value => patchSection('constraints', { bid_change_limit_pct: value })} />
            </>}
            {draft.permissions.price && <>
              <NumberField label='Макс. шаг цены, %' hint='На сколько процентов цена может измениться за одно действие.' value={draft.constraints.price_change_limit_pct} onChange={value => patchSection('constraints', { price_change_limit_pct: value })} />
            </>}
            <NumberField label='Бюджет проверок, ₽' hint='Считается только сверхрасход относительно уровня до проверки. Исчерпание останавливает новые эксперименты.' value={draft.constraints.experiment_budget} onChange={value => patchSection('constraints', { experiment_budget: value })} />
          </div>

          {/* Тринадцать полей подряд читаются как анкета. На виду остаются те,
              без которых рычаг не работает; остальные сворачиваются — но только
              в ручном режиме, мастер остаётся как был. */}
          <Collapsible open={!manual}
            summary='Тонкая настройка: ДРР и рентабельность'>
            <div className='oz-auto__fields'>
              <NumberField label='Предельный общий ДРР за день, %' hint='Вся реклама группы / вся выручка её заказов за сегодня, в процентах: 15 — это 15%. Пока дневной ДРР выше предела, ставки не поднимаются. Можно не заполнять: контроль окупаемости работает и без него.' value={draft.constraints.target_drr ?? ''} onChange={value => patchSection('constraints', { target_drr: value })} />
              <NumberField label='Целевая рентабельность, %' hint='Прибыль ÷ себестоимость — как в «Экономике». Ниже цели система предпочитает снижать ставку и поднимать цену; если ставка уже на минимуме, она поднимается в пределах минимальной рентабельности.' value={draft.constraints.target_margin_pct ?? ''} onChange={value => patchSection('constraints', { target_margin_pct: value })} />
              <NumberField label='Минимальная рентабельность, %' hint='Прибыль ÷ себестоимость — как в «Экономике». Ниже этого уровня ставку не поднимают и цену не снижают; для распродажи затоваривания — только с вашего согласования.' value={draft.constraints.min_margin_pct ?? ''} onChange={value => patchSection('constraints', { min_margin_pct: value })} />
              <NumberField label='Значимое улучшение, %' hint='Минимальное изменение цели, ради которого стоит сохранять новые настройки.' value={draft.evaluation.meaningful_change_pct} onChange={value => patchSection('evaluation', { meaningful_change_pct: value })} />
            </div>
          </Collapsible>
          {draft.permissions.price && <PriceCorridor
            products={selectedProducts} context={priceContext} error={priceError}
            onApply={applyPriceCorridor} />}
          {selectedProducts.length > 1 && <PerSkuLimits draft={draft} patch={patch}
            products={selectedProducts} context={priceContext} contextError={priceError} />}

          <label className='oz-auto__field'>
            <span><input type='checkbox' checked={Boolean(draft.constraints.auto_rollback)}
              onChange={e => patchSection('constraints', { auto_rollback: e.target.checked })} /> Возвращать изменение, если результат оказался ухудшением</span>
            <small>Возврат выполняется только после закрытого окна наблюдения с выводом «ухудшение». Задачу на инфографику вернуть нельзя.</small>
          </label>
          <div className='oz-auto__notice'>
            {draft.execution_mode === 'AUTO'
              ? 'В автономном режиме система сама выбирает одну гипотезу за цикл, применяет её к одной паре РК–товар или к одному товару, ведёт окно наблюдения и классифицирует результат. Одновременно проверяется только один рычаг. Все изменения записываются в журнал и ограничены полями выше.'
              : draft.execution_mode === 'CONTROLLED'
                ? 'Система готовит конкретное действие и ждёт вашего подтверждения в карточке автоматизации.'
                : 'В наблюдении изменения в кабинет не отправляются: видны только состояние, гипотеза и карта вероятностей.'}
          </div>
        </div>}

        {sectionHead(5, 'Название и запуск')}
        {manual && showSection(5) && <div className='oz-auto__section'>
          <h4>Проверьте задание</h4>
          <label className='oz-auto__field'><span>Название</span><input value={draft.name} onChange={e => patch({ name: e.target.value })}
            placeholder={`${strategyLabel(draft.strategy_type) || 'Автоматизация'} · ${selectedProducts[0]?.model_name || ''}`} /></label>
          <div className='oz-auto__review'>
            <div><small>Товары</small><strong>{selectedProducts.length}</strong><span>{[...new Set(selectedProducts.map(item => item.model_name))].join(', ')}</span></div>
            <div><small>Стратегия</small><strong>{strategyLabel(draft.strategy_type)}</strong><span>{{ AUTO: 'Автономный режим', CONTROLLED: 'Проверки с подтверждением', OBSERVE: 'Наблюдение' }[draft.execution_mode]}</span></div>
            <div><small>Воздействия</small><strong>{Object.entries(draft.permissions).filter(([, value]) => value).map(([key]) => ({ bid: 'ставки', price: 'цены' }[key])).filter(Boolean).join(', ') || 'только анализ'}</strong><span>Только выбранные объекты управления</span></div>
          </div>
          <div className='oz-auto__notice'>После создания откройте статистику и действия. Существующая история доступна сразу; при изменении ставки сохраняется её снимок. Недостаток данных не считается ухудшением.</div>
        </div>}

        {!manual && step === 5 && <GroupsSection
          groups={groups} groupOverrides={groupOverrides} groupCoverage={groupCoverage}
          isGroupSelected={isGroupSelected} setGroupToggle={setGroupToggle} setGroupToggles={setGroupToggles}
          setGroupStrategy={setGroupStrategy} setGroupConstraint={setGroupConstraint}
          clearGroupOverride={clearGroupOverride}
          openGroupKey={openGroupKey} setOpenGroupKey={setOpenGroupKey}
          strategies={strategies} strategyLabel={strategyLabel}
          defaultStrategyType={draft.strategy_type} />}

        {!manual && step === 6 && <SummarySection
          groups={selectedGroups} totalGroups={groups.length} groupOverrides={groupOverrides}
          strategyLabel={strategyLabel}
          defaultStrategyType={draft.strategy_type} missingProducts={missingProducts}
          onBuildPlan={buildCampaignPlan} planLoading={campaignPlanLoading}
          plan={campaignPlan} planError={campaignPlanError}
          onApplyPlan={applyCampaignPlan} applyLoading={campaignApplyLoading}
          applyResult={campaignApplyResult} />}

        <footer className='oz-auto__footer'>
          {editor === 'WIZARD' && step > 1 && <button type='button' onClick={() => stepAfter(-1)}>Назад</button>}
          {/* Отказ приходил наверху страницы, у кнопки сохранения — ничего:
              со стороны это выглядит как «кнопка не работает». */}
          <span className='oz-auto__footer-note'>
            {error ? <em className='oz-auto__hint is-action'>{error}</em>
              : manual && objectiveGaps().length ? <em className='oz-auto__hint is-action'>
                Не сохранится: осталось заполнить {objectiveGaps().join(', ')}</em> : null}
          </span>
          {editor === 'WIZARD' && step < WIZARD_TOTAL_STEPS
            ? <button type='button' className='oz-auto__primary' disabled={!canContinue()} onClick={() => stepAfter(1)}>Продолжить</button>
            : editor === 'WIZARD'
              ? <button type='button' className='oz-auto__primary' disabled={batchSaving || !selectedGroups.length} onClick={saveBatch}>
                {batchSaving ? `Создаём…${batchProgress ? ` ${batchProgress}` : ''}` : `Создать ${selectedGroups.length} автоматизаций`}</button>
              : <button type='button' className='oz-auto__primary' disabled={saving || !draft.sku_ids.length || !draft.strategy_type} onClick={save}>{saving ? 'Сохраняем…' : editingId ? 'Сохранить настройки' : draft.status === 'DRAFT' ? 'Сохранить черновик' : 'Создать автоматизацию'}</button>}
        </footer>
      </section> : listView === 'table' ? <OzonAutomationsTable accountId={accountId} request={request}
        strategies={strategies} onChanged={load}
        onOpen={id => { changeListView('cards'); setExpandedId(id); }} /> : <div className='oz-auto__list'>
        {automations.map(item => <div className='oz-auto__card-wrap' key={item.id} id={`oz-auto-group-${item.id}`}><article className='oz-auto__card'>
          <div><span className={`oz-auto__status is-${item.status.toLowerCase()}`}>{item.status === 'ACTIVE' ? ({ AUTO: 'Автономный', CONTROLLED: 'С подтверждением', OBSERVE: 'Наблюдение' }[item.execution_mode] || item.execution_mode) : item.status === 'PAUSED' ? 'Пауза' : 'Черновик'}</span>
            <h3>{item.name}</h3><p>{strategyLabel(item.strategy_type)} · {item.sku_ids.length} тов.</p>
          {/* Пять строк подряд читаются как шум. Остаются две: в каком этапе
              автоматизация и единственное, что сейчас важнее всего. Подробности
              ждут внутри — за кнопкой «Открыть». */}
          <div className='oz-auto__card-state'>
            <strong>{item.status !== 'ACTIVE' ? 'Управление приостановлено'
              : item.latest_action ? ({ OBSERVING: 'Наблюдаем результат изменения', UNCONFIRMED: 'Нужна сверка с OZON', APPLYING: 'Отправка изменения', COMPLETED: 'Проверка завершена', INTERRUPTED: 'Проверка прервана' }[item.latest_action.status] || item.latest_action.status)
              : (item.target_mode !== 'AUTO' && !item.control_targets?.length) ? 'Выберите РК в настройках'
                : item.overview?.state === 'BASELINE_READY' ? 'Базовый уровень готов' : 'Собираем базовый уровень'}</strong>
            {(() => {
              // Причина паузы важнее всего; затем то, что мешает работать;
              // затем — сколько данных накоплено.
              if (item.paused_reason) {
                return <span className='oz-auto__hint is-action'>{item.paused_reason}</span>;
              }
              if (item.readiness?.headline && !item.readiness.ready) {
                return <span className='oz-auto__hint is-action'>{item.readiness.headline}</span>;
              }
              if (item.status !== 'ACTIVE') return <span>Наблюдение остановлено</span>;
              return <span>{item.overview?.state === 'BASELINE_READY'
                ? `Данных: ${item.overview.data_days} дн.`
                : `${item.overview?.data_days || 0} из ${item.overview?.required_days || 0} дней данных`}</span>;
            })()}
          </div></div>

          <GoalMetric goal={item.overview?.goal} />
          <div className='oz-auto__card-actions'><button type='button' onClick={() => setExpandedId(value => value === item.id ? null : item.id)}>{expandedId === item.id ? 'Свернуть' : 'Открыть'}</button>
            <button type='button' onClick={() => openEditor('MANUAL', item)}>Настройки</button>
            <button type='button' onClick={() => changeStatus(item)}>{item.status === 'ACTIVE' ? 'Пауза' : 'Возобновить'}</button>
            <button type='button' onClick={() => removeGroup(item)}>Удалить</button></div>
        </article>{expandedId === item.id && <OzonAutomationDashboard key={`${item.id}-${deepTab?.at || 0}`}
          item={item} accountId={accountId} request={request} onChanged={load}
          initialTab={String(item.id) === String(expandedId) ? deepTab?.tab : null} />}</div>)}
        {!automations.length && <div className='oz-auto__empty'><strong>Автоматизаций пока нет</strong><span>Создайте первую через мастер: кабинетные данные подставятся, а вы выберете цель и пределы.</span></div>}
      </div>}
    </div>
  );
}

const PER_SKU_FIELDS = [
  ['stock', 'Остаток, шт.'],
  ['max_bid', 'Макс. ставка, ₽'],
  ['min_price', 'Мин. цена, ₽'],
  ['current_price', 'Текущая цена, ₽'],
  ['max_price', 'Макс. цена, ₽'],
  ['target_drr', 'Предельный общий ДРР за день, %'],
  ['min_margin_pct', 'Мин. рент., %'],
  ['current_margin', 'Тек. рент., %'],
];

// Показываются, но не редактируются: остаток и цена — из кабинета OZON,
// рентабельность — из Unit-экономики. Вводить их руками значит завести
// второй ответ на вопрос, у которого уже есть один.
const READ_ONLY_FIELDS = ['stock', 'current_price', 'current_margin'];

function PerSkuLimits({ draft, patch, products, context, contextError }) {
  const [open, setOpen] = useState(false);
  const perSku = draft.constraints.per_sku || {};
  const bySku = Object.fromEntries(
    (context?.items || []).map(item => [String(item.sku_id), item]));
  const setValue = (skuId, key, value) => {
    const row = { ...(perSku[skuId] || {}) };
    if (value === '') delete row[key]; else row[key] = value;
    const next = { ...perSku };
    if (Object.keys(row).length) next[skuId] = row; else delete next[skuId];
    patch({ constraints: { ...draft.constraints, per_sku: next } });
  };
  const overrideCount = Object.keys(perSku).length;
  return <div className='oz-auto__per-sku'>
    <button type='button' onClick={() => setOpen(value => !value)}>
      {open ? 'Скрыть персональные границы' : 'Задать границы отдельным товарам'}
      {overrideCount > 0 ? ` (${overrideCount})` : ''}
    </button>
    <small>У товаров одной модели бывает разная себестоимость и разные условия торговли.
      Пустое поле — действует общая граница сверху.</small>
    {open && <small>Текущая цена — цена продавца из кабинета OZON, та самая, которую
      ограничивают мин. и макс. цена. Текущая рентабельность — из «Экономика →
      Unit-экономика» за 30 дней, и она известна только там, где заполнена
      себестоимость. Обе колонки только для справки и не редактируются.</small>}
    {contextError && <small role='alert'>{contextError}</small>}
    {open && <div className='oz-auto__table-scroll'><table>
      <thead><tr><th>Товар</th>{PER_SKU_FIELDS.map(([key, label]) =>
        <th key={key}>{label}</th>)}</tr></thead>
      <tbody>{products.map(product => <tr key={product.sku_id}>
        <td><strong>{product.offer_id || product.sku_id}</strong>
          <small>{product.name}</small></td>
        {PER_SKU_FIELDS.map(([key]) => key === 'stock'
          ? <td key={key} className='oz-auto__readonly-cell'>
            <StockBadge fbo={product.stock_fbo} fbs={product.stock_fbs} />
          </td>
          : READ_ONLY_FIELDS.includes(key)
          // Не границы, а числа, вокруг которых границы и задаются: без них
          // коридор выставляется вслепую. Меняются они не здесь.
          ? <td key={key} className='oz-auto__readonly-cell'>
            <ContextValue field={key} row={bySku[String(product.sku_id)]}
              loading={!context && !contextError} error={contextError}
              period={context?.margin_period} />
          </td>
          : ['min_price', 'max_price'].includes(key) && bySku[String(product.sku_id)]?.price_owner
          ? <td key={key} className='oz-auto__readonly-cell'>
            <small className='oz-auto__unavailable'>ценой ведёт «{bySku[String(product.sku_id)].price_owner}»</small>
          </td>
          : <td key={key}>
            <input type='number' min='0' step='any'
              value={(perSku[product.sku_id] || {})[key] ?? ''}
              placeholder={draft.constraints[key] ?? '—'}
              onChange={e => setValue(product.sku_id, key, e.target.value)} />
            {key === 'min_price' && bySku[String(product.sku_id)]?.map_corridor && <small>
              РРЦ, диапазон: витрина {bySku[String(product.sku_id)].map_corridor.mrc} – {bySku[String(product.sku_id)].map_corridor.rrc} ₽</small>}
            {/* Рубли заданы, а думают в процентах — показываем и то и то. */}
            {['min_price', 'max_price'].includes(key) && (() => {
              const price = bySku[String(product.sku_id)]?.price;
              const bound = Number((perSku[product.sku_id] || {})[key]
                ?? draft.constraints[key]);
              if (!price || !Number.isFinite(bound) || !bound) return null;
              const shift = (bound - price) / price * 100;
              return <small>{shift > 0 ? '+' : ''}{shift.toFixed(1)}%</small>;
            })()}
          </td>)}
      </tr>)}</tbody>
    </table></div>}
  </div>;
}

function PriceCorridor({ products, context, error, onApply, compact = false }) {
  const [down, setDown] = useState(10);
  const [up, setUp] = useState(10);
  const [result, setResult] = useState('');
  const bySku = Object.fromEntries(
    (context?.items || []).map(item => [String(item.sku_id), item]));
  const known = products.filter(product => bySku[String(product.sku_id)]?.price != null
    && !bySku[String(product.sku_id)]?.price_owner);
  const preview = known.slice(0, 3).map(product => {
    const price = bySku[String(product.sku_id)].price;
    return `${product.offer_id || product.sku_id}: ${Math.round(price * (1 - down / 100))} — ${Math.round(price * (1 + up / 100))} ₽`;
  });

  return <div className={`oz-auto__corridor${compact ? ' is-compact' : ''}`}>
    <h5>Коридор цены в процентах</h5>
    {!compact && <p>Проще, чем выписывать рубли каждому товару: задайте, насколько
      система может опустить и поднять цену относительно текущей, и проставьте всем
      сразу.</p>}
    <div className='oz-auto__corridor-row'>
      <label><span>Ниже текущей, %</span>
        <input type='number' min='0' max='90' value={down}
          onChange={e => { setDown(e.target.value); setResult(''); }} /></label>
      <label><span>Выше текущей, %</span>
        <input type='number' min='0' max='300' value={up}
          onChange={e => { setUp(e.target.value); setResult(''); }} /></label>
      <button type='button' disabled={!known.length}
        title={known.length ? '' : 'Цены ещё не получены от OZON'}
        onClick={() => {
          const applied = onApply(down, up);
          setResult(applied
            ? `Границы проставлены: товаров ${applied}`
            : 'Не удалось: нет текущих цен');
        }}>
        {products.length === 1 ? 'Проставить границы' : 'Проставить всем выбранным товарам'}</button>
    </div>
    {error && <small role='alert'>{error}</small>}
    {!error && !context && <small>Получаем текущие цены из кабинета OZON…</small>}
    {context && known.length < products.length && <small>
      Цена известна у {known.length} из {products.length} товаров — остальным границы
      придётся задать вручную.</small>}
    {preview.length > 0 && <small>Получится: {preview.join('; ')}
      {known.length > preview.length ? ` и ещё ${known.length - preview.length}` : ''}</small>}
    {result && <small className='oz-auto__corridor-done'>{result}</small>}
    {!compact && <small>Проценты пересчитываются в рубли сразу и сохраняются
      рублями. Так и задумано: коридор, который каждый раз считается от текущей цены,
      съезжает вниз вслед за каждым снижением и перестаёт удерживать цену. Изменится
      цена в кабинете — нажмите кнопку ещё раз.</small>}
  </div>;
}

function ContextValue({ field, row, loading, error, period }) {
  if (error) return <span title={error}>ошибка</span>;
  if (loading) return <span>…</span>;
  if (field === 'current_price') {
    if (!row || row.price == null) {
      return <span title='OZON не вернул цену по этому артикулу'>нет данных</span>;
    }
    return <span title={row.ozon_min_price
      ? `Минимальная цена в кабинете OZON: ${row.ozon_min_price} ₽`
      : 'Цена продавца из кабинета OZON'}>
      {Number(row.price).toLocaleString('ru-RU', { maximumFractionDigits: 2 })}</span>;
  }
  if (!row || row.margin_pct == null) {
    return <span title={row && !row.cogs_known
      ? 'Не заполнена себестоимость в разделе «Экономика → Unit-экономика → Себестоимость»'
      : 'За период нет продаж по этому товару'}>
      {row && !row.cogs_known ? 'нет с/с' : 'нет продаж'}</span>;
  }
  return <span title={period ? `Период: ${period.from} — ${period.to}` : ''}>
    {Number(row.margin_pct).toFixed(1)}</span>;
}

function GoalMetric({ goal }) {
  // Процент показывается только там, где пользователь задал порог. У целей без
  // числового порога процента не существует — вместо него текущий уровень, а
  // выдуманное «87% выполнено» было бы хуже пустого места.
  if (!goal) return <div className='oz-auto__goal' />;
  const value = goal.current;
  const format = number => Number(number).toLocaleString('ru-RU', { maximumFractionDigits: 1 });
  if (goal.pct != null) {
    const tone = goal.pct >= 100 ? 'is-done' : goal.pct >= 70 ? 'is-close' : 'is-far';
    return <div className={`oz-auto__goal ${tone}`}>
      <strong>{format(goal.pct)}%</strong>
      <small>цели достигнуто</small>
      <span>{goal.label}: {format(value)} из {format(goal.target)}</span>
    </div>;
  }
  if (value != null) {
    return <div className='oz-auto__goal'>
      <strong>{format(value)}</strong>
      <small>{goal.label}</small>
      <span>{goal.has_threshold ? 'порог не задан' : 'цель без числового порога'}</span>
    </div>;
  }
  return <div className='oz-auto__goal is-unknown'>
    <strong>—</strong><small>{goal.label}</small>
    <span>{goal.note || 'нет данных'}</span>
  </div>;
}

// Свернуть можно не везде: в мастере те же поля должны остаться на виду.
function Collapsible({ open, summary, children }) {
  if (open) return children;
  return <details className='oz-auto__advanced'>
    <summary>{summary}</summary>{children}</details>;
}

function Hint({ text }) {
  return <i className='oz-auto__help' title={text} aria-label={text}>?</i>;
}

function fMoneyBidder(value) {
  if (value == null) return '—';
  return `${Number(value).toLocaleString('ru-RU')} ₽`;
}

function GroupsSection({
  groups, groupOverrides, groupCoverage, isGroupSelected, setGroupToggle, setGroupToggles,
  setGroupStrategy, setGroupConstraint, clearGroupOverride,
  openGroupKey, setOpenGroupKey, strategies, strategyLabel, defaultStrategyType,
}) {
  const overrideFields = [
    ['max_bid', 'Максимальная ставка, ₽'],
    ['min_price', 'Минимальная цена, ₽'],
    ['max_price', 'Максимальная цена, ₽'],
    ['target_margin_pct', 'Целевая рентабельность, %'],
  ];
  const selectedCount = groups.filter(group => isGroupSelected(group.key)).length;
  const allSelected = groups.length > 0 && selectedCount === groups.length;
  const selectAll = () => setGroupToggles(Object.fromEntries(groups.map(group => [group.key, true])));
  const selectNone = () => setGroupToggles(Object.fromEntries(groups.map(group => [group.key, false])));
  return <div className='oz-auto__section'>
    <h4>Группы автоматизации<Hint text='Галочка — создавать группу или нет. Если товары группы уже встречаются в другой автоматизации, под карточкой появится предупреждение — решать, создавать или нет, всё равно вам.' /></h4>
    {!groups.length && <p className='oz-auto__notice'>Товары не выбраны — вернитесь на первый шаг.</p>}
    {groups.length > 0 && <div className='oz-auto__bulk-select'>
      <span>Отмечено: {selectedCount} из {groups.length}</span>
      <button type='button' onClick={selectAll} disabled={allSelected}>Отметить все</button>
      <button type='button' onClick={selectNone} disabled={!selectedCount}>Снять все</button>
    </div>}
    <div className='oz-auto__groups-list'>
      {groups.map(group => {
        const override = groupOverrides[group.key] || {};
        const open = openGroupKey === group.key;
        const overridden = Boolean(override.strategy_type) || Boolean(override.constraints);
        const coverage = groupCoverage[group.key];
        const fullyCovered = coverage && coverage.overlap === coverage.total;
        const selected = isGroupSelected(group.key);
        return <div className={`oz-auto__group-card${open ? ' is-open' : ''}${selected ? '' : ' is-skipped'}`} key={group.key}>
          <div className='oz-auto__group-head'>
            <input type='checkbox' checked={selected}
              onChange={e => setGroupToggle(group.key, e.target.checked)}
              onClick={e => e.stopPropagation()}
              title={selected ? 'Снять — эту группу не создавать' : 'Отметить — создать эту группу'} />
            <button type='button' className='oz-auto__group-head-toggle'
              onClick={() => setOpenGroupKey(open ? null : group.key)}>
              <em>{open ? '▾' : '▸'}</em>
              <strong>{group.name}</strong>
              <span>{group.sku_ids.length} тов.</span>
              <span>{strategyLabel(override.strategy_type || defaultStrategyType)}</span>
              {overridden && <b className='oz-auto__group-flag'>своё</b>}
            </button>
          </div>
          {coverage && <p className={`oz-auto__group-coverage${fullyCovered ? ' is-full' : ''}`}>
            {fullyCovered
              ? `Все товары уже встречаются в автоматизации «${coverage.name}» — проверьте, не дублируете ли управление.`
              : `${coverage.overlap} из ${coverage.total} товаров уже в «${coverage.name}» — остальные не охвачены.`}
          </p>}
          {open && <div className='oz-auto__group-body'>
            <label className='oz-auto__field'><span>Стратегия для этой группы</span>
              <select value={override.strategy_type || ''}
                onChange={e => setGroupStrategy(group.key, e.target.value)}>
                <option value=''>Как по умолчанию — {strategyLabel(defaultStrategyType)}</option>
                {strategies.map(strategy => <option key={strategy.key} value={strategy.key}>{strategy.title}</option>)}
              </select>
            </label>
            <div className='oz-auto__fields'>
              {overrideFields.map(([key, label]) => <NumberField key={key} label={label}
                value={override.constraints?.[key] ?? ''}
                onChange={value => setGroupConstraint(group.key, key, value)} />)}
            </div>
            {overridden && <button type='button' onClick={() => clearGroupOverride(group.key)}>
              Сбросить к умолчанию</button>}
          </div>}
        </div>;
      })}
    </div>
  </div>;
}

function SummarySection({
  groups, totalGroups, groupOverrides, strategyLabel, defaultStrategyType, missingProducts,
  onBuildPlan, planLoading, plan, planError, onApplyPlan, applyLoading, applyResult,
}) {
  const totalSkus = groups.reduce((sum, group) => sum + group.sku_ids.length, 0);
  const skippedCount = totalGroups - groups.length;
  return <div className='oz-auto__section'>
    <h4>Итог</h4>
    <div className='oz-auto__review'>
      <div><small>Групп будет создано</small><strong>{groups.length}</strong>
        {skippedCount > 0 && <span>{skippedCount} пропущено на шаге «Группы»</span>}</div>
      <div><small>Товаров всего</small><strong>{totalSkus}</strong></div>
      <div><small>Стратегия по умолчанию</small><strong>{strategyLabel(defaultStrategyType)}</strong></div>
    </div>
    {!groups.length && <div className='oz-auto__notice is-action'>Все группы сняты на шаге
      «Группы» — создавать нечего. Вернитесь и отметьте хотя бы одну.</div>}
    <div className='oz-auto__table-scroll'><table>
      <thead><tr><th>Группа</th><th>Товаров</th><th>Стратегия</th></tr></thead>
      <tbody>{groups.map(group => <tr key={group.key}>
        <td>{group.name}</td>
        <td>{group.sku_ids.length}</td>
        <td>{strategyLabel(groupOverrides[group.key]?.strategy_type || defaultStrategyType)}</td>
      </tr>)}</tbody>
    </table></div>
    {missingProducts.length > 0 ? <div className='oz-auto__notice is-action'>
      <p>Без рекламных кампаний: <strong>{missingProducts.length}</strong> из {totalSkus} товаров.
        Без РК ставками управлять нечем — можно создать их сразу по правилам «Генератора РК»:
        минимальная ставка, Поиск и Поиск и рекомендации.</p>
      {!plan && <button type='button' onClick={onBuildPlan} disabled={planLoading}>
        {planLoading ? 'Считаем план…' : 'Создать РК автоматически'}</button>}
      {planError && <small role='alert'>{planError}</small>}
      {plan && !applyResult && <div>
        <p>Новых РК: {plan.summary?.total_new_campaigns ?? '—'} ·
          минимальный бюджет: {fMoneyBidder(plan.summary?.total_budget_min)}</p>
        <button type='button' onClick={onApplyPlan} disabled={applyLoading}>
          {applyLoading ? 'Создаём РК…' : 'Подтвердить создание РК'}</button>
      </div>}
      {applyResult && (() => {
        const createdCount = Array.isArray(applyResult.created) ? applyResult.created.length : Number(applyResult.created || 0);
        const errorList = applyResult.errors || [];
        return <div>
          <p className={errorList.length ? 'oz-auto__hint is-action' : 'oz-auto__hint'}>
            Создано РК: {createdCount}{errorList.length ? `, ошибок: ${errorList.length}` : ''}.
            {createdCount > 0 && !errorList.length
              ? ' Автоматизации ниже подхватят их сами — режим «все кампании товара».' : ''}
          </p>
          {errorList.length > 0 && <ul className='oz-auto__error-list'>
            {errorList.slice(0, 10).map((item, index) => <li key={index}>
              {item.name || item.ozon_campaign_id || 'РК'} — {item.error}</li>)}
            {errorList.length > 10 && <li>и ещё {errorList.length - 10}</li>}
          </ul>}
        </div>;
      })()}
    </div> : <div className='oz-auto__notice'>У всех выбранных товаров уже есть рекламные кампании.</div>}
    <div className='oz-auto__notice'>После создания откройте карточки автоматизаций для тонкой настройки и статистики.</div>
  </div>;
}

// Остаток нужен, чтобы отличить мёртвый товар (ноль на всех складах) от
// живого прежде, чем включать его в группу — на него строится реклама,
// а ноль означает, что показывать всё равно нечего.
function StockBadge({ fbo, fbs }) {
  if (fbo == null && fbs == null) return <small>Остаток неизвестен</small>;
  const total = (fbo || 0) + (fbs || 0);
  return <small className={total > 0 ? 'oz-auto__rk-ok' : 'oz-auto__rk-none'}
    title={`FBO: ${fbo ?? 0} · FBS: ${fbs ?? 0}`}>
    Остаток: {total}{total === 0 ? ' — нет на складах' : ''}
  </small>;
}

function NumberField({ label, hint, value, onChange }) {
  return <label><span>{label}{hint && <i className='oz-auto__help' title={hint} aria-label={hint}>?</i>}</span><input type='number' min='0' value={value} onChange={e => onChange(e.target.value)} /></label>;
}

function ObjectiveFields({ draft, patchSection, spec }) {
  const required = spec?.required_objective || [];
  const labels = {
    ORDERS_GROWTH: 'Цель без числового порога: система ищет прирост заказов в пределах ограничителей.',
    PROFITABILITY: 'Цель без числового порога: система поднимает процент рентабельности.',
    NET_PROFIT: 'Цель без числового порога: система ищет максимум прибыли в рублях.',
    PROFIT_GROWTH: 'Цель без числового порога: система наращивает абсолютную прибыль.',
    CLEAR_STOCK: 'Укажите дату, к которой остаток должен закончиться.',
    HOLD_POSITION: 'Цель без числового порога: система удерживает достигнутый уровень заказов.',
  };
  const byImpressions = draft.objective.position_source === 'IMPRESSIONS';
  const valueLabel = {
    PROFITABILITY_GROWTH: 'Целевая рентабельность, %',
    SELL_THROUGH: 'Продать, шт.',
    LEADERSHIP: byImpressions ? 'Целевые показы в день, шт.' : 'Целевая позиция в поиске',
    LEADERSHIP_ANY_COST: byImpressions ? 'Целевые показы в день, шт.' : 'Целевая позиция в поиске',
    REDUCE: 'Целевой уровень',
    TURNOVER_CAP: 'Потолок оборота, ₽',
  }[draft.strategy_type] || 'Целевое значение';

  if (!required.length && draft.strategy_type !== 'TURNOVER_CAP') {
    return <p className='oz-auto__simple-goal'>
      {labels[draft.strategy_type] || 'Цель сохраняется в задании.'} Результат измеряется в:
      {' '}{spec?.goal_label || 'выбранном показателе'}.
    </p>;
  }
  return <div className='oz-auto__fields'>
    {required.includes('target_value') && <NumberField label={valueLabel}
      hint='Значение, относительно которого система считает цель достигнутой.'
      value={draft.objective.target_value}
      onChange={value => patchSection('objective', { target_value: value })} />}
    {required.includes('target_date') && <label><span>К какой дате</span>
      <input type='date' value={draft.objective.target_date}
        onChange={e => patchSection('objective', { target_date: e.target.value })} /></label>}
    {draft.strategy_type === 'TURNOVER_CAP' && <>
      <NumberField label='Период потолка, дней' hint='За какой период считается потолок оборота.'
        value={draft.objective.period_days ?? 30}
        onChange={value => patchSection('objective', { period_days: value })} />
      <label><span>В пределах лимита</span>
        <select value={draft.objective.secondary_goal}
          onChange={e => patchSection('objective', { secondary_goal: e.target.value })}>
          <option value='PROFIT'>Максимизировать прибыль</option>
          <option value='PROFITABILITY'>Максимизировать рентабельность</option>
        </select></label>
    </>}
    {spec?.requires_position && <label><span>Чем измеряем лидерство</span>
      <select value={draft.objective.position_source || 'POSITION'}
        onChange={e => patchSection('objective', { position_source: e.target.value })}>
        <option value='POSITION'>Позицией в поиске — нужен мониторинг позиций</option>
        <option value='IMPRESSIONS'>Показами — приближение охвата, мониторинг не нужен</option>
      </select>
      <small>Позиция — это настоящий ранг карточки по запросу. Показы зависят от
        бюджетов конкурентов и лидерство измеряют грубо. Смешивать их внутри одного
        окна наблюдения система не будет.</small></label>}
    {draft.strategy_type === 'REDUCE' && <label><span>Что сокращаем</span>
      <select value={draft.objective.reduce_target || 'orders'}
        onChange={e => patchSection('objective', { reduce_target: e.target.value })}>
        <option value='orders'>Заказы</option>
        <option value='spend'>Рекламный расход</option>
      </select></label>}
  </div>;
}
